import useContainer from "../../../../utils/hooks/inventory/getContainer";
import {
  ContainerDetails,
  SubTitle,
  ChildrenGroup,
} from "./InventoryUpdatesStyles";
import { InputLabel, Pill } from "../../../../constants/Styles";
import React, { ReactNode, useEffect } from "react";
import BinInfo from "../../BoardInventory/BinInfo";
import AllocationsSummary from "./AllocationsSummary";
import { datetimeStringToHumanReadableDatetime } from "../../../../utils/valueFormatting";
import ContainerHistoryTable from "../ContainerHistory";
import { usePaginatedContainerHistory } from "../../../../utils/hooks/inventory/containerHistory";
import { ContainerLink } from "../BarcodeLink";
import Icon from "../../../common/Icon";

export default function ContainerSummary({
  containerBarcode,
  onError,
  children,
  displayAllocations,
  displayHistory,
  onUpdate,
}: {
  containerBarcode: string;
  onError?: (detail: string) => void;
  children?: ReactNode;
  displayAllocations?: boolean;
  displayHistory?: boolean;
  onUpdate?: () => void;
}) {
  const { container, containerNotFound, refreshContainer } =
    useContainer(containerBarcode);
  const { containerHistory } = usePaginatedContainerHistory(containerBarcode);

  useEffect(() => {
    if (containerNotFound) {
      onError?.("Container does not exist");
    }
  }, [containerNotFound, onError]);

  const renderPartOnFeederStatus = () => {
    if (container === null) {
      return <Pill width={"5rem"} />;
    } else if (container.part_has_containers_on_feeders) {
      return (
        <div>
          <Icon src={"/icons/check_green.svg"} alt={"check"} />
        </div>
      );
    } else {
      return (
        <div>
          <Icon src={"/icons/fatal.svg"} alt={"X"} />
        </div>
      );
    }
  };
  return (
    <ContainerDetails>
      <div>
        <InputLabel>Container barcode:</InputLabel>
        <div>
          <ContainerLink containerBarcode={containerBarcode} />
        </div>
      </div>
      <div>
        <InputLabel>Part:</InputLabel>
        <div>{container?.part_name || <Pill width={"5rem"} />}</div>
      </div>
      <div>
        <InputLabel>Part has container(s) on feeders:</InputLabel>
        {renderPartOnFeederStatus()}
      </div>

      <div>
        <InputLabel>Last update:</InputLabel>
        <div>
          {!container ? (
            <Pill width={"5rem"} />
          ) : container.most_recent_update !== null ? (
            datetimeStringToHumanReadableDatetime(container.most_recent_update)
          ) : (
            "Unknown"
          )}
        </div>
      </div>
      <div>
        <InputLabel>Last counted amount:</InputLabel>
        <div>
          {container !== null ? (
            container.last_counted_amount.toLocaleString()
          ) : (
            <Pill width={"5rem"} />
          )}
        </div>
        <InputLabel>Active allocations:</InputLabel>
        <div>
          {container !== null ? (
            container.active_allocations_total.toLocaleString()
          ) : (
            <Pill width={"5rem"} />
          )}
        </div>
        <InputLabel>Spent allocations:</InputLabel>
        <div>
          {container !== null ? (
            container.spent_allocations_total.toLocaleString()
          ) : (
            <Pill width={"5rem"} />
          )}
        </div>
      </div>
      <div>
        {container !== null ? (
          <BinInfo
            binName={container.bin_name}
            binLocation={container.bin_location}
            binBarcode={container.flashpcb_bin_barcode}
            binType={container.bin_type}
            canAllocateFromBin={container.can_allocate_from_bin}
          />
        ) : (
          <div>
            <InputLabel>Bin info:</InputLabel>
            <div>
              <Pill width={"5rem"} />
            </div>
          </div>
        )}
      </div>
      <ChildrenGroup>
        {children}
        {displayAllocations &&
          container &&
          container.part_allocations.length > 0 && (
            <AllocationsSummary
              container={container}
              onUpdate={() => {
                refreshContainer();
                onUpdate?.();
              }}
            />
          )}
        {displayHistory &&
          containerHistory !== null &&
          containerHistory.records.length > 0 && (
            <div>
              <SubTitle>Container history</SubTitle>
              <ContainerHistoryTable containerHistory={containerHistory} />
            </div>
          )}
      </ChildrenGroup>
    </ContainerDetails>
  );
}
